import { useState, useMemo } from 'react';
import {
  Button,
  Typography,
  Card,
  CardContent,
  CardActions,
} from '@mui/material';
import Grid from '@mui/material/Grid';

const months = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

interface MonthSelectorProps {
  initialMonths?: number[];
  onApply: (months: number[]) => void;
  onClose: () => void;
}

export const MonthSelector = ({
  initialMonths = [],
  onApply,
  onClose,
}: MonthSelectorProps) => {
  const [startMonth, setStartMonth] = useState<number | null>(
    initialMonths.length > 0 ? initialMonths[0] : null
  );
  const [endMonth, setEndMonth] = useState<number | null>(
    initialMonths.length > 0 ? initialMonths[initialMonths.length - 1] : null
  );

  const selectedRange = useMemo(() => {
    if (startMonth === null) return [];
    const end = endMonth ?? startMonth;
    const start = Math.min(startMonth, end);
    const finalEnd = Math.max(startMonth, end);
    const range: number[] = [];
    for (let i = start; i <= finalEnd; i++) {
      range.push(i);
    }
    return range;
  }, [startMonth, endMonth]);

  const handleMonthClick = (month: number) => {
    if (startMonth === null || (startMonth !== null && endMonth !== null)) {
      setStartMonth(month);
      setEndMonth(null);
    } else {
      setEndMonth(month);
    }
  };

  const handleReset = () => {
    setStartMonth(null);
    setEndMonth(null);
  };

  const handleApply = () => {
    onApply(selectedRange);
    onClose();
  };

  const getSubtitle = () => {
    if (startMonth === null) {
        return 'Click to select start month';
    }
    if (endMonth === null) {
        return `${months[startMonth - 1]} - ?`;
    }
    const start = Math.min(startMonth, endMonth);
    const end = Math.max(startMonth, endMonth);
    return `${months[start-1]} - ${months[end-1]}`;
  }

  return (
    <Card sx={{ minWidth: 275, maxWidth: 360 }}>
      <CardContent>
        <Typography variant="h6" component="div" sx={{ mb: 1 }}>
          Select Month Range
        </Typography>
        <Typography sx={{ mb: 2, color: 'text.secondary' }}>
          {getSubtitle()}
        </Typography>
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'medium' }}>
          Selected Months
        </Typography>
        <Grid container spacing={1}>
          {months.map((month, index) => {
            const monthNumber = index + 1;
            const isSelected = selectedRange.includes(monthNumber);
            return (
              <Grid size={4} key={month}>
                <Button
                  fullWidth
                  variant={isSelected ? 'contained' : 'outlined'}
                  color="primary"
                  onClick={() => handleMonthClick(monthNumber)}
                  sx={{
                    backgroundColor: isSelected ? 'common.black' : 'transparent',
                    color: isSelected ? 'common.white' : 'common.black',
                    '&:hover': {
                        backgroundColor: isSelected ? 'common.black' : 'action.hover',
                    }
                  }}
                >
                  {month}
                </Button>
              </Grid>
            );
          })}
        </Grid>
      </CardContent>
      <CardActions sx={{ justifyContent: 'space-between', padding: 2 }}>
        <Button variant="outlined" onClick={handleReset}>
          Reset
        </Button>
        <Button
          variant="contained"
          onClick={handleApply}
          sx={{
            backgroundColor: 'common.black',
            color: 'common.white',
            '&:hover': {
                backgroundColor: 'grey.800',
            }
          }}
        >
          Apply
        </Button>
      </CardActions>
    </Card>
  );
};
